import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import Wordmark from "./Wordmark";
import { ArrowUpRight } from "lucide-react";

const links = [
  { label: "Product", href: "#features" },
  { label: "Security", href: "#security" },
  { label: "Roles", href: "#roles" },
  { label: "Showcase", href: "#showcase" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "py-3" : "py-5"
        }`}
      >
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div
            className={`flex items-center justify-between rounded-2xl px-5 py-3 transition-all duration-500 ${
              scrolled
                ? "glass shadow-[0_8px_40px_rgba(0,0,0,0.4)]"
                : "bg-transparent border border-transparent"
            }`}
          >
            <Link to="/" data-testid="navbar-logo-link" className="flex items-center">
              <Wordmark size="lg" />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-8">
              {isHome && links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className="text-sm text-[#9BB8B8] hover:text-white transition-colors duration-200"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/partners"
                className="text-sm text-[#9BB8B8] hover:text-white transition-colors duration-200"
              >
                Partners
              </Link>
            </nav>

            <div className="flex items-center gap-2">
              {/* Desktop CTAs */}
              <a
                href="https://nexus-vs.vawick.com/signin"
                data-testid="navbar-demo-cta"
                className="hidden lg:inline-flex group items-center gap-1.5 text-sm font-medium bg-white text-black px-4 py-2 rounded-full hover:bg-[#FF4F00] hover:text-white transition-all duration-300"
              >
                Request Demo
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>

              {/* Hamburger button — mobile only */}
              <button
                onClick={() => setIsOpen((o) => !o)}
                aria-label={isOpen ? "Close menu" : "Open menu"}
                aria-expanded={isOpen}
                className="lg:hidden relative w-11 h-11 flex flex-col items-center justify-center gap-[6px]"
              >
                <motion.span
                  animate={isOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="block w-5 h-0.5 bg-white rounded-full origin-center"
                />
                <motion.span
                  animate={isOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
                  transition={{ duration: 0.25 }}
                  className="block w-5 h-0.5 bg-white rounded-full origin-center"
                />
                <motion.span
                  animate={isOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="block w-5 h-0.5 bg-white rounded-full origin-center"
                />
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 top-0 z-40 pt-24 pb-10 bg-[#060C0C]/95 backdrop-blur-xl border-b border-[#152222] lg:hidden"
          >
            <nav className="flex flex-col px-6">
              {isHome && links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setIsOpen(false)}
                  className="font-display text-xl font-medium py-4 border-b border-[#152222] text-[#9BB8B8] hover:text-white transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/partners"
                onClick={() => setIsOpen(false)}
                className="font-display text-xl font-medium py-4 border-b border-[#152222] text-[#9BB8B8] hover:text-white transition-colors"
              >
                Partners
              </Link>
              <div className="mt-8 flex flex-col gap-3">
                <a
                  href="https://nexus-vs.vawick.com/signin"
                  onClick={() => setIsOpen(false)}
                  className="group inline-flex items-center justify-center gap-1.5 text-sm font-medium bg-white text-black px-4 py-3.5 rounded-full hover:bg-[#FF4F00] hover:text-white transition-all duration-300"
                >
                  Request Access
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
